import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  Alert,
  Box,
  Button,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import PaymentGatewayApi from "../../../infrastructure/api/paymentGateway";
import PaymentStatusBadge from "./PaymentStatusBadge";
import {
  formatRial,
  mapOperationalStateToBadge,
  normalizeRequestInvoicesPanel,
  pick,
} from "./paymentGatewayUtils";

const copyText = async (text) => {
  if (!text) return;
  try {
    await navigator.clipboard.writeText(String(text));
    toast.success("کپی شد");
  } catch {
    toast.error("کپی ناموفق بود");
  }
};

const RequestInvoicesPaymentPanel = ({ requestInfoId, compact = false }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [invoices, setInvoices] = useState([]);

  const load = useCallback(async () => {
    if (!requestInfoId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await PaymentGatewayApi.getRequestInvoicesPaymentPanel(
        requestInfoId
      );
      const { error: err, data } = normalizeRequestInvoicesPanel(res);
      if (err) {
        setError(err);
        setInvoices([]);
      } else {
        setInvoices(pick(data, "invoices", "Invoices") ?? []);
      }
    } catch {
      setError("بارگذاری فاکتورها ناموفق بود.");
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  }, [requestInfoId]);

  useEffect(() => {
    load();
  }, [load]);

  if (!requestInfoId) return null;

  if (loading) {
    return (
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Skeleton height={28} width="40%" />
        <Skeleton height={48} sx={{ mt: 1 }} />
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Alert severity="warning" action={<Button onClick={load}>تلاش مجدد</Button>}>
          {error}
        </Alert>
      </Paper>
    );
  }

  if (invoices.length === 0) {
    return null;
  }

  return (
    <Paper variant="outlined" sx={{ p: compact ? 1.5 : 2, borderRadius: 2 }}>
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
        فاکتورها و پرداخت
      </Typography>
      <Stack spacing={1.25}>
        {invoices.map((inv) => {
          const invoiceId = pick(inv, "invoiceId", "InvoiceId");
          const opState = pick(inv, "operationalState", "OperationalState");
          const opTitle = pick(inv, "operationalStateTitle", "OperationalStateTitle");
          const tracking = pick(inv, "providerTrackingCode", "ProviderTrackingCode");
          const showPay = pick(inv, "showPayAction", "ShowPayAction");

          return (
            <Box
              key={invoiceId}
              sx={{
                p: 1.25,
                borderRadius: 1.5,
                border: 1,
                borderColor: "divider",
              }}
            >
              <Stack spacing={1}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  spacing={1}
                >
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="body2" fontWeight={600} noWrap>
                      {pick(inv, "title", "Title") || `فاکتور #${invoiceId}`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      مانده: {formatRial(pick(inv, "remainingAmount", "RemainingAmount"))}
                    </Typography>
                  </Box>
                  <PaymentStatusBadge
                    state={mapOperationalStateToBadge(opState)}
                    label={opTitle}
                  />
                </Stack>

                {tracking ? (
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography variant="caption" color="text.secondary">
                      کد پیگیری: {tracking}
                    </Typography>
                    <Tooltip title="کپی کد پیگیری">
                      <IconButton
                        size="small"
                        onClick={() => copyText(tracking)}
                        aria-label="کپی"
                      >
                        <ContentCopyIcon fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                ) : null}

                {showPay ? (
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    endIcon={<ChevronLeftIcon />}
                    sx={{ minHeight: 44 }}
                    onClick={() =>
                      navigate(`/SLN/Customer/InvoicePayment/${invoiceId}`)
                    }
                  >
                    پرداخت
                  </Button>
                ) : null}
              </Stack>
            </Box>
          );
        })}
      </Stack>
    </Paper>
  );
};

RequestInvoicesPaymentPanel.propTypes = {
  requestInfoId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  compact: PropTypes.bool,
};

export default RequestInvoicesPaymentPanel;
